import { useState, useEffect } from 'react';
import { 
  ArrowLeft, 
  Edit, 
  Trash2, 
  CalendarPlus, 
  Download, 
  ExternalLink,
  ChevronDown
} from 'lucide-react';
import { Countdown } from '../types/database';
import { downloadICS, getGoogleCalendarUrl } from '../utils/calendar';

// Color palette matching CountdownCard
const colorPalette = [
  '#FF6B6B', // Coral
  '#E91E8C', // Rose
  '#6BAF92', // Sage
  '#F4A435', // Gold
  '#FF8C00', // Orange
  '#9B8EC4', // Lavender
  '#5B9BD5', // Sky
  '#E8A0BF', // Blush
]

const categoryColors: Record<string, string> = {
  Birthday: '#FCE4EC',
  Holiday: '#FFF8E1',
  Travel: '#E8F5E9',
  School: '#EDE7F6',
  Personal: '#E3F2FD',
  Work: '#E8F5E9',
  Other: '#F3E5F5',
}

const legacyColorMap: Record<string, string> = {
  '#FF8C69': '#6BAF92', // old Peach → Sage
};

interface CountdownDetailProps {
  countdown: Countdown;
  onClose: () => void;
  onEdit: (countdown: Countdown) => void;
  onDelete: (id: string) => void;
}

export function CountdownDetail({ countdown, onClose: onBack, onEdit, onDelete }: CountdownDetailProps) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [showCalendarMenu, setShowCalendarMenu] = useState(false);

  useEffect(() => {
    const calculate = () => {
      const target = new Date(countdown.event_date).getTime();
      const now = new Date().getTime();
      const difference = target - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60),
        });
      }
    };

    calculate();
    const timer = setInterval(calculate, 1000);
    return () => clearInterval(timer);
  }, [countdown.event_date]);

  const eventDate = new Date(countdown.event_date);
  const isPast = eventDate.getTime() < new Date().setHours(0, 0, 0, 0);
  const formattedDate = eventDate.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  // Build the background style
  const raw = countdown.color ?? '';
  const resolvedColor = legacyColorMap?.[raw] ?? raw;
  const cardColor = (resolvedColor && colorPalette.includes(resolvedColor)) 
    ? resolvedColor 
    : (categoryColors[countdown.category] ?? '#F3E5F5');

  const backgroundStyle = countdown.background_image_url 
    ? { backgroundImage: `url(${countdown.background_image_url})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { backgroundColor: cardColor };

  return (
    <div className="fixed inset-0 z-[60] min-h-screen relative overflow-hidden animate-fadeIn" style={backgroundStyle}>
      {countdown.background_image_url && (
        <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px]" />
      )}

      {/* Fixed nav bar */}
      <div className="fixed top-0 left-0 right-0 z-10 flex items-center justify-between px-4 sm:px-8 py-4">
        <button 
          onClick={onBack}
          className="p-3 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-white" />
        </button>
        
        <div className="flex gap-2">
          <div className="relative">
            <button 
              onClick={() => setShowCalendarMenu(!showCalendarMenu)}
              className={`p-3 rounded-full backdrop-blur-sm transition-all flex items-center gap-2 ${
                showCalendarMenu ? 'bg-white text-[#1a3e54]' : 'bg-white/20 text-white hover:bg-white/30'
              }`}
              title="Add to Calendar"
            >
              <CalendarPlus className="w-5 h-5" />
              <span className="hidden md:inline text-xs font-bold uppercase tracking-wider pr-1">Remind Me</span>
              <ChevronDown className={`w-3 h-3 transition-transform ${showCalendarMenu ? 'rotate-180' : ''}`} />
            </button>

            {showCalendarMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-2xl border border-[#1a3e54]/5 overflow-hidden animate-scaleIn origin-top-right">
                <button 
                  onClick={() => {
                    window.open(getGoogleCalendarUrl(countdown), '_blank');
                    setShowCalendarMenu(false);
                  }}
                  className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-gray-50 transition-colors group"
                >
                  <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                    <ExternalLink className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-[#1a3e54]">Google Calendar</p>
                    <p className="text-[10px] text-[#1a3e54]/40 uppercase font-black">Open in Browser</p>
                  </div>
                </button>
                <div className="h-px bg-[#1a3e54]/5 mx-4" />
                <button 
                  onClick={() => {
                    downloadICS(countdown);
                    setShowCalendarMenu(false);
                  }}
                  className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-gray-50 transition-colors group"
                >
                  <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center group-hover:bg-purple-100 transition-colors">
                    <Download className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-[#1a3e54]">Apple / Outlook</p>
                    <p className="text-[10px] text-[#1a3e54]/40 uppercase font-black">Download .ics File</p>
                  </div>
                </button>
              </div>
            )}
          </div>

          <button 
            onClick={() => onEdit(countdown)}
            className="p-3 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-colors"
          >
            <Edit className="w-5 h-5 text-white" />
          </button>
          <button 
            onClick={() => onDelete(countdown.id)}
            className="p-3 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-colors"
          >
            <Trash2 className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="relative flex flex-col items-center justify-center min-h-screen pt-28 pb-12 px-4 z-0">
        {countdown.background_image_url ? (
          <div className="mb-6 w-24 h-24 sm:w-32 sm:h-32 rounded-2xl overflow-hidden shadow-lg border-4 border-white/20">
            <img src={countdown.background_image_url} alt="" className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="text-7xl sm:text-9xl mb-6 drop-shadow-2xl animate-bounce">
            {countdown.emoji || '🎯'}
          </div>
        )}
        
        <h1 className="text-4xl sm:text-6xl lg:text-7xl font-bold text-white text-center mb-3 drop-shadow-lg font-playfair px-4">
          {countdown.name}
        </h1>
        <p className="text-white/80 text-lg sm:text-xl mb-10 text-center font-medium drop-shadow-sm">{formattedDate}</p>

        {isPast ? (
          <div className="text-2xl sm:text-4xl font-bold text-white/90 bg-white/10 px-8 py-4 rounded-3xl backdrop-blur-sm">
            🎉 This day has passed!
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-3 sm:gap-4 md:gap-6 w-full max-w-2xl px-4">
            {[
              { label: 'Days', value: timeLeft.days },
              { label: 'Hours', value: timeLeft.hours },
              { label: 'Minutes', value: timeLeft.minutes },
              { label: 'Seconds', value: timeLeft.seconds },
            ].map(({ label, value }) => (
              <div key={label} className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 sm:p-6 md:p-8 flex flex-col items-center shadow-xl border border-white/10">
                <span className="text-3xl sm:text-5xl md:text-6xl font-bold text-white tabular-nums">
                  {String(value).padStart(2, '0')}
                </span>
                <span className="text-white/70 text-[10px] sm:text-xs mt-2 uppercase font-black tracking-[0.2em]">{label}</span>
              </div>
            ))}
          </div>
        )}

        {!isPast && (
          <button 
            onClick={() => setShowCalendarMenu(!showCalendarMenu)}
            className="mt-12 flex items-center gap-3 px-8 py-4 bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-2xl text-white font-bold text-xs uppercase tracking-[0.2em] transition-all border border-white/10"
          >
            <CalendarPlus className="w-5 h-5" />
            Add to my Calendar
          </button>
        )}
      </div>
    </div>
  );
}